import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Undo2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import toast, { Toaster } from 'react-hot-toast';
import { getAuthApiUrl } from '../lib/apiConfig';

interface ProductBrief {
  id: number;
  name?: string;
  main_image?: string | null;
  mystery_image?: string | null;
}

interface OrderLine {
  id: number;
  product: ProductBrief;
  quantity: number;
  unit_price: string;
  total_price: string;
  display_name: string;
  display_image: string | null;
  is_mystery_item: boolean;
}

interface OrderDetail {
  id: number;
  order_number: string;
  status: string;
  payment_status: string;
  subtotal: string;
  shipping_cost: string;
  tax_amount: string;
  total_amount: string;
  shipping_address: string;
  shipping_phone: string;
  waybill_number: string;
  carrier_name: string;
  tracking_url: string;
  items: OrderLine[];
  can_track_package: boolean;
  return_eligible: boolean;
  has_open_return: boolean;
  created_at: string;
}

export default function ProfileOrderDetail() {
  const { orderId } = useParams<{ orderId: string }>();
  const { token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [order, setOrder] = useState<OrderDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [returnOpen, setReturnOpen] = useState(false);
  const [returnReason, setReturnReason] = useState('');
  const [returnSubmitting, setReturnSubmitting] = useState(false);

  const authHeaders = useCallback(() => {
    const t = token ?? localStorage.getItem('authToken');
    return {
      'Content-Type': 'application/json',
      ...(t ? { Authorization: `Token ${t}` } : {}),
    };
  }, [token]);

  const loadOrder = useCallback(async () => {
    if (!orderId) return;
    setLoading(true);
    try {
      const res = await fetch(getAuthApiUrl(`/api/orders/${orderId}/`), {
        headers: authHeaders(),
      });
      if (res.status === 404) {
        toast.error('Order not found');
        navigate('/profile/orders', { state: location.state });
        return;
      }
      if (!res.ok) {
        const errText = await res.text().catch(() => '');
        console.error('Order detail failed', res.status, errText);
        toast.error('Could not load order. Please try again.');
        navigate('/profile/orders', { state: location.state });
        return;
      }
      const data = await res.json();
      setOrder(data);
    } catch {
      toast.error('Network error');
      navigate('/profile/orders', { state: location.state });
    } finally {
      setLoading(false);
    }
  }, [orderId, authHeaders, navigate]);

  useEffect(() => {
    loadOrder();
  }, [loadOrder]);

  const formatMoney = (v: string) => {
    const n = Number(v);
    if (Number.isNaN(n)) return v;
    return `₹${n.toFixed(2)}`;
  };

  const handleTrackPackage = () => {
    if (!order) return;
    const url = (order.tracking_url || '').trim();
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }
    const wb = (order.waybill_number || '').trim();
    if (wb) {
      toast(
        'A tracking link is not set for this shipment yet. Check again shortly, or contact support with your waybill number.',
        { duration: 5000 }
      );
      return;
    }
    toast(
      'This order has not shipped yet. Once it ships, you can track the journey from here.',
      { duration: 5000 }
    );
  };

  const submitReturn = async () => {
    if (!order || !orderId) return;
    const reason = returnReason.trim();
    if (reason.length < 3) {
      toast.error('Please enter a short reason (at least 3 characters).');
      return;
    }
    setReturnSubmitting(true);
    try {
      const res = await fetch(
        getAuthApiUrl(`/api/orders/${orderId}/request-return/`),
        {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({ reason }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data.message || data.detail || 'Could not submit return');
        return;
      }
      toast.success(
        data.message ||
          'Your return has been submitted. After careful checks, the refund will be credited to your original payment method.'
      );
      setReturnOpen(false);
      setReturnReason('');
      await loadOrder();
    } catch {
      toast.error('Network error');
    } finally {
      setReturnSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-hero">
      <Toaster
        position="top-center"
        toastOptions={{
          duration: 4000,
          style: {
            background: 'rgba(255, 255, 255, 0.1)',
            color: '#ffffff',
            border: '1px solid rgba(0, 212, 255, 0.3)',
            backdropFilter: 'blur(8px)',
          },
        }}
      />

      <div className="container mx-auto px-4 py-6 max-w-2xl">
        <div className="flex items-center gap-4 mb-8">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/profile/orders', { state: location.state })}
            className="hover:bg-gray-800/50"
            aria-label="Back to orders"
          >
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gradient">Order details</h1>
            {order && (
              <p className="text-gray-400 mt-1 text-sm">
                {order.order_number} · {order.status.replace(/_/g, ' ')}
              </p>
            )}
          </div>
        </div>

        {loading || !order ? (
          <div className="cosmic-card p-10 text-center text-gray-400">Loading…</div>
        ) : (
          <div className="space-y-6">
            <div className="cosmic-card p-5 space-y-3">
              <p className="text-sm text-gray-400">Shipping address</p>
              <p className="text-white whitespace-pre-wrap text-sm">{order.shipping_address}</p>
              <p className="text-sm text-gray-400">Phone: {order.shipping_phone}</p>
              {(order.waybill_number || order.carrier_name) && (
                <div className="pt-2 border-t border-gray-700/50 text-sm">
                  {order.carrier_name && (
                    <p className="text-gray-400">
                      Carrier: <span className="text-white">{order.carrier_name}</span>
                    </p>
                  )}
                  {order.waybill_number && (
                    <p className="text-gray-400">
                      Waybill: <span className="text-white">{order.waybill_number}</span>
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                type="button"
                className="gap-2 flex-1"
                onClick={handleTrackPackage}
              >
                <ExternalLink size={16} />
                Track package
              </Button>
              <Button
                type="button"
                variant="secondary"
                className="gap-2 flex-1"
                disabled={!order.return_eligible}
                onClick={() => setReturnOpen(true)}
              >
                <Undo2 size={16} />
                Return / refund
              </Button>
            </div>
            {!order.return_eligible && (
              <p className="text-xs text-gray-500 px-1">
                {order.has_open_return
                  ? 'A return is already in progress for this order.'
                  : order.status !== 'delivered'
                    ? 'Returns are available after your order is delivered.'
                    : 'This order is not eligible for a return right now.'}
              </p>
            )}

            <div className="cosmic-card p-5 space-y-4">
              <h2 className="text-lg font-semibold text-white">Items</h2>
              <ul className="space-y-4">
                {order.items.map((line) => (
                  <li
                    key={line.id}
                    className="flex gap-4 border-b border-gray-700/40 pb-4 last:border-0 last:pb-0"
                  >
                    <div className="w-16 h-16 rounded-lg bg-gray-800/50 overflow-hidden shrink-0">
                      {line.display_image ? (
                        <img
                          src={line.display_image}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-600 text-xs">
                          —
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium text-sm">{line.display_name}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        Qty {line.quantity} · {formatMoney(line.unit_price)} each
                      </p>
                      <p className="text-sm text-electric-cyan mt-1">
                        {formatMoney(line.total_price)}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
              <div className="pt-4 border-t border-gray-700/50 space-y-1 text-sm">
                <div className="flex justify-between text-gray-400">
                  <span>Subtotal</span>
                  <span>{formatMoney(order.subtotal)}</span>
                </div>
                <div className="flex justify-between text-gray-400">
                  <span>Shipping</span>
                  <span>{formatMoney(order.shipping_cost)}</span>
                </div>
                <div className="flex justify-between text-white font-semibold pt-2">
                  <span>Total</span>
                  <span>{formatMoney(order.total_amount)}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {returnOpen && order && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="return-dialog-title"
        >
          <div className="cosmic-card p-6 w-full max-w-md space-y-4 shadow-xl border border-electric-cyan/20">
            <h2 id="return-dialog-title" className="text-lg font-semibold text-white">
              Return / refund
            </h2>
            <p className="text-sm text-gray-400">
              Order {order.order_number}. After approval, refunds go to your original payment method.
            </p>
            <div className="space-y-2">
              <Label htmlFor="return-reason">Reason</Label>
              <Input
                id="return-reason"
                value={returnReason}
                onChange={(e) => setReturnReason(e.target.value)}
                placeholder="Brief reason for return"
                maxLength={500}
              />
            </div>
            <div className="flex gap-3 justify-end pt-2">
              <Button
                variant="secondary"
                type="button"
                onClick={() => {
                  setReturnOpen(false);
                  setReturnReason('');
                }}
                disabled={returnSubmitting}
              >
                Cancel
              </Button>
              <Button type="button" onClick={submitReturn} disabled={returnSubmitting}>
                {returnSubmitting ? 'Submitting…' : 'Submit request'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
